import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { tasksAPI, operatorsAPI, contactsAPI } from '../services/api';
import { FiCheck, FiTrash2, FiPlus, FiClock, FiAlertCircle, FiUser, FiUsers, FiX } from 'react-icons/fi';
import { formatDateTime } from '../utils/formatters';
import toast from 'react-hot-toast';

const PRIORITY = { high: { label: 'Yuqori', cls: 'badge-danger' }, normal: { label: "O'rta", cls: 'badge-info' }, low: { label: 'Past', cls: 'badge-neutral' } };
const TYPE = { call: "Qo'ng'iroq", follow_up: 'Follow-up', meeting: 'Uchrashuv', email: 'Email' };

export default function TasksPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState({});
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('');       // '' | today | overdue
  const [assigned, setAssigned] = useState('');  // '' | me | unassigned
  const [status, setStatus] = useState('open');
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status };
      if (scope) params.scope = scope;
      if (assigned) params.assigned_to = assigned;
      const [t, s] = await Promise.all([tasksAPI.list(params), tasksAPI.summary().catch(() => null)]);
      setTasks(t.data.tasks || []);
      if (s) setSummary(s.data.summary || {});
    } catch (e) { toast.error('Vazifalarni olishda xato'); }
    finally { setLoading(false); }
  }, [scope, assigned, status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { operatorsAPI.list().then(r => setOperators(r.data.operators || [])).catch(() => {}); }, []);

  const complete = async (id) => {
    try { await tasksAPI.complete(id); toast.success('Bajarildi'); load(); }
    catch { toast.error('Xato'); }
  };
  const remove = async (id) => {
    if (!window.confirm("Vazifa o'chirilsinmi?")) return;
    try { await tasksAPI.delete(id); load(); } catch { toast.error('Xato'); }
  };

  const isOverdue = (t) => t.status === 'open' && t.due_at && new Date(t.due_at) < new Date();

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Vazifalar</h1>
          <p className="page-subtitle">Follow-up va qayta aloqa vazifalari</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}><FiPlus size={16} /> Vazifa</button>
      </div>

      {/* Summary chips */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <Chip icon={FiClock} label="Bugun" value={summary.today} color="#3b82f6" active={scope === 'today'} onClick={() => setScope(scope === 'today' ? '' : 'today')} />
        <Chip icon={FiAlertCircle} label="Kechikkan" value={summary.overdue} color="#ef4444" active={scope === 'overdue'} onClick={() => setScope(scope === 'overdue' ? '' : 'overdue')} />
        <Chip icon={FiUser} label="Mening" value={summary.my_open} color="#8b5cf6" active={assigned === 'me'} onClick={() => setAssigned(assigned === 'me' ? '' : 'me')} />
        <Chip icon={FiUsers} label="Egasiz" value={summary.unassigned} color="#f59e0b" active={assigned === 'unassigned'} onClick={() => setAssigned(assigned === 'unassigned' ? '' : 'unassigned')} />
      </div>

      <div className="filters" style={{ marginBottom: 16 }}>
        <select className="input" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="open">Ochiq</option>
          <option value="done">Bajarilgan</option>
          <option value="all">Barchasi</option>
        </select>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? <div className="loading-center"><div className="spinner" /></div> : (
          <div className="table-wrapper"><table><thead><tr>
            <th style={{ width: 40 }}></th><th>Vazifa</th><th>Kontakt</th><th>Muddat</th>
            <th>Ustuvorlik</th><th>Tur</th><th>Mas'ul</th><th></th>
          </tr></thead><tbody>
            {tasks.length === 0 ? (
              <tr><td colSpan="8" style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>Vazifalar yo'q</td></tr>
            ) : tasks.map(t => (
              <tr key={t.id} style={t.status === 'done' ? { opacity: 0.55 } : undefined}>
                <td>
                  {t.status === 'open' ? (
                    <button className="btn btn-ghost btn-icon" title="Bajarildi" onClick={() => complete(t.id)}
                      style={{ color: '#10b981' }}><FiCheck size={18} /></button>
                  ) : <FiCheck size={16} style={{ color: '#10b981' }} />}
                </td>
                <td style={{ fontWeight: 600 }}>{t.title}
                  {t.description && <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 400 }}>{t.description}</div>}
                </td>
                <td>{t.contact_name || (t.contact_phone ? t.contact_phone : '—')}</td>
                <td style={{ whiteSpace: 'nowrap', color: isOverdue(t) ? '#ef4444' : 'var(--text-secondary)', fontWeight: isOverdue(t) ? 700 : 400 }}>
                  {t.due_at ? formatDateTime(t.due_at) : '—'}
                </td>
                <td><span className={`badge ${(PRIORITY[t.priority] || PRIORITY.normal).cls}`}>{(PRIORITY[t.priority] || {}).label || t.priority}</span></td>
                <td>{TYPE[t.type] || t.type}</td>
                <td>{t.assignee_name || <span style={{ color: 'var(--text-muted)' }}>Egasiz</span>}</td>
                <td><button className="btn btn-ghost btn-icon" onClick={() => remove(t.id)} style={{ color: 'var(--text-muted)' }}><FiTrash2 size={15} /></button></td>
              </tr>
            ))}
          </tbody></table></div>
        )}
      </div>

      {showForm && <TaskForm operators={operators} isAdmin={isAdmin} selfId={user?.id} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function Chip({ icon: Icon, label, value, color, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
      background: active ? `${color}22` : 'var(--bg-secondary)', border: `1px solid ${active ? color : 'var(--border)'}`,
      color: 'var(--text-primary)', fontFamily: 'inherit',
    }}>
      <Icon size={16} color={color} />
      <span style={{ fontSize: 13 }}>{label}</span>
      <span style={{ fontWeight: 700 }}>{value ?? 0}</span>
    </button>
  );
}

function TaskForm({ operators, isAdmin, selfId, onClose, onSaved }) {
  const [form, setForm] = useState({ title: '', description: '', due_at: '', priority: 'normal', type: 'follow_up', assigned_to: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Kontakt tanlash (qidiruvli)
  const [contact, setContact] = useState(null);          // tanlangan kontakt {id,name,phone}
  const [cQuery, setCQuery] = useState('');
  const [cResults, setCResults] = useState([]);
  const [cSearching, setCSearching] = useState(false);

  useEffect(() => {
    if (contact) return;                                  // tanlangan bo'lsa qidirmaymiz
    const q = cQuery.trim();
    if (!q) { setCResults([]); return; }
    setCSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await contactsAPI.list({ search: q, limit: 8 });
        setCResults(res.data.contacts || []);
      } catch { setCResults([]); }
      finally { setCSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [cQuery, contact]);

  const pickContact = (c) => { setContact(c); setCQuery(''); setCResults([]); };
  const clearContact = () => { setContact(null); setCQuery(''); setCResults([]); };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Sarlavha majburiy'); return; }
    setSaving(true);
    try {
      await tasksAPI.create({
        ...form,
        contact_id: contact ? contact.id : null,
        due_at: form.due_at ? new Date(form.due_at).toISOString() : null,
        // Operator uchun mas'ul o'zi (backend ham majburlaydi); admin tanlaganini yuboradi
        assigned_to: isAdmin ? (form.assigned_to || null) : selfId,
      });
      toast.success('Vazifa yaratildi');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'Xato'); }
    finally { setSaving(false); }
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>Yangi vazifa</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><FiX size={18} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input className="input" placeholder="Sarlavha *" value={form.title} onChange={e => set('title', e.target.value)} />
          <textarea className="input" placeholder="Tavsif / izoh" rows={2} value={form.description} onChange={e => set('description', e.target.value)} />

          {/* Kontakt tanlash */}
          <label style={modalStyles.label}>Kontakt</label>
          {contact ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--bg-primary,#0f172a)', border: '1px solid var(--border,#2d3a4f)', borderRadius: 8 }}>
              <FiUser size={14} color="#3b82f6" />
              <span style={{ flex: 1, fontSize: 14 }}>{contact.name}{contact.phone ? ` · ${contact.phone}` : ''}</span>
              <button className="btn btn-ghost btn-icon" onClick={clearContact} title="Bekor qilish"><FiX size={15} /></button>
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              <input className="input" placeholder="Kontakt qidirish (ism yoki raqam)..." value={cQuery} onChange={e => setCQuery(e.target.value)} />
              {(cResults.length > 0 || cSearching) && (
                <div style={modalStyles.dropdown}>
                  {cSearching && cResults.length === 0 ? (
                    <div style={{ padding: '10px 12px', fontSize: 13, color: 'var(--text-muted)' }}>Qidirilmoqda…</div>
                  ) : cResults.map(c => (
                    <button key={c.id} type="button" style={modalStyles.dropdownItem} onClick={() => pickContact(c)}>
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{c.name}</span>
                      {c.phone && <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{c.phone}</span>}
                    </button>
                  ))}
                  {!cSearching && cResults.length === 0 && cQuery.trim() && (
                    <div style={{ padding: '10px 12px', fontSize: 13, color: 'var(--text-muted)' }}>Topilmadi</div>
                  )}
                </div>
              )}
            </div>
          )}

          <label style={modalStyles.label}>Muddat</label>
          <input className="input" type="datetime-local" value={form.due_at} onChange={e => set('due_at', e.target.value)} />
          <div style={{ display: 'flex', gap: 12 }}>
            <select className="input" value={form.priority} onChange={e => set('priority', e.target.value)}>
              <option value="high">Yuqori</option><option value="normal">O'rta</option><option value="low">Past</option>
            </select>
            <select className="input" value={form.type} onChange={e => set('type', e.target.value)}>
              <option value="follow_up">Follow-up</option><option value="call">Qo'ng'iroq</option>
              <option value="meeting">Uchrashuv</option><option value="email">Email</option>
            </select>
          </div>
          {isAdmin && (
            <select className="input" value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)}>
              <option value="">Mas'ul: egasiz</option>
              {operators.map(o => <option key={o.id} value={o.id}>{o.full_name}</option>)}
            </select>
          )}
          <button className="btn btn-primary" onClick={save} disabled={saving} style={{ marginTop: 4 }}>
            {saving ? 'Saqlanmoqda...' : 'Saqlash'}
          </button>
        </div>
      </div>
    </div>
  );
}

const modalStyles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(3px)' },
  modal: { background: 'var(--bg-secondary, #1e293b)', border: '1px solid var(--border, #2d3a4f)', borderRadius: 16, padding: 24, width: 420, maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  label: { fontSize: 12, color: 'var(--text-muted)', marginBottom: -6 },
  dropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 10,
    background: 'var(--bg-secondary,#1e293b)', border: '1px solid var(--border,#2d3a4f)',
    borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', maxHeight: 220, overflowY: 'auto',
  },
  dropdownItem: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, width: '100%',
    padding: '9px 12px', background: 'transparent', border: 'none', borderBottom: '1px solid var(--border,#2d3a4f)',
    color: 'var(--text-primary)', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
  },
};
